/**
 * Copyright 2024-2025 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

import createRealmPath from '@forgerock/platform-shared/src/utils/createRealmPath';
import { generateAmApi } from '@forgerock/platform-shared/src/api/BaseApi';

function authorizationPolicyApiConfig(realm) {
  const realmPath = createRealmPath(realm);
  const path = realmPath ? `realms/root/${realmPath}/policies` : 'realms/root/policies';
  return {
    path,
    apiVersion: 'protocol=2.0,resource=1.0',
  };
}

/**
 * Creates a new AM policy on the 'apps' policy set
 * @param {String} realm Realm name
 * @param {*} payload Policy object to create
 */
export function createAuthorizationPolicy(realm, payload) {
  return generateAmApi(authorizationPolicyApiConfig(realm)).post(
    '/?_action=create',
    payload,
    { withCredentials: true },
  );
}

/**
 * Deletes an existing AM policy on the 'apps' policy set
 * @param {String} realm Realm name
 */
export function deleteAuthorizationPolicy(realm, policyName) {
  return generateAmApi(authorizationPolicyApiConfig(realm)).delete(
    `/${policyName}`,
    { withCredentials: true },
  );
}

/**
 * Returns an object containing a policy attached to specified application
 * @param {String} realm Realm name
 * @param {String} appName Application name
 * @returns {Promise} Resolves with an individual policy object
 */
export function getAuthorizationPolicy(realm, appName) {
  return generateAmApi(authorizationPolicyApiConfig(realm)).get(
    `/${appName}`,
    { withCredentials: true },
  );
}

/**
 * Updates an existing AM policy
 * @param {*} realm Realm name
 * @param {*} appName Application name
 * @param {*} payload Policy object to update
 * @returns {Promise} Resolves with an individual policy object
 */
export function updateAuthorizationPolicy(realm, appName, payload) {
  return generateAmApi(authorizationPolicyApiConfig(realm)).put(
    `/${appName}`,
    payload,
    { withCredentials: true },
  );
}

/**
 * Evaluates a policy tree
 * @param {String} realm Realm name
 * @param {*} payload Policy tree to evaluate
 * @returns {Promise} Resolves with an array of policy objects
 */
export function evaluateAuthorizationPolicy(realm, payload) {
  return generateAmApi(authorizationPolicyApiConfig(realm)).post(
    '/?_action=evaluateTree',
    payload,
    { withCredentials: true },
  );
}
